import { Link } from "react-router-dom";
import { Phone, MapPin, Facebook, Clock, Mail } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="inline-block">
              <img
                src="https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/uwv1ms6g_cglancey%20double%20glazing.png"
                alt="C. Glancey Glazing | Windows & Doors"
                className="h-24 sm:h-28 lg:h-32 w-auto object-contain"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Your trusted local specialists in Lanarkshire for high-performance
              double glazing, uPVC windows, composite doors, and garden rooms.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href="https://www.facebook.com/CGlanceyJoinersGlazing"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 bg-[#FF6B00] flex items-center justify-center hover:bg-[#E65000] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@cglanceywindows/photo/7534480014209846550?image_index=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                className="w-10 h-10 bg-[#FF6B00] flex items-center justify-center hover:bg-[#E65000] transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-black text-lg uppercase tracking-wide mb-6">
              Our Services
            </h3>
            <ul className="space-y-3">
              {[
                { label: "Double Glazing Windows", path: "/windows" },
                { label: "Composite & uPVC Doors", path: "/doors" },
                { label: "Garden Rooms", path: "/outbuilds" },
                { label: "Recent Work", path: "/recent-work" },
                { label: "Customer Reviews", path: "/reviews" },
                { label: "FAQ - Motherwell & Glasgow", path: "/faq" },
                { label: "Find Us Online", path: "/find-us" },
                { label: "Contact Us", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(" ", "-")}`}
                    className="text-gray-400 hover:text-[#FF6B00] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-black text-lg uppercase tracking-wide mb-6">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:07917181597"
                  data-testid="footer-phone"
                  className="flex items-center space-x-3 text-gray-400 hover:text-[#FF6B00] transition-colors"
                >
                  <Phone className="w-5 h-5 text-[#FF6B00]" />
                  <span>07917 181597</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:c.glanceyglaziers@gmail.com"
                  data-testid="footer-email"
                  className="flex items-center space-x-3 text-gray-400 hover:text-[#FF6B00] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#FF6B00]" />
                  <span>c.glanceyglaziers@gmail.com</span>
                </a>
              </li>
              <li className="flex items-start space-x-3 text-gray-400">
                <MapPin className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                <span>
                  Serving Lanarkshire, Glasgow,
                  <br />
                  and surrounding areas
                </span>
              </li>
              <li className="flex items-center space-x-3 text-gray-400">
                <Clock className="w-5 h-5 text-[#FF6B00]" />
                <span>Fast & Reliable Service</span>
              </li>
            </ul>
          </div>

          {/* Areas We Cover */}
          <div>
            <h3 className="font-black text-lg uppercase tracking-wide mb-6">
              Areas We Cover
            </h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              {[
                "Motherwell",
                "Bellshill",
                "Hamilton",
                "Wishaw",
                "Glasgow",
                "Lanarkshire",
                "Newton Mearns",
                "East Kilbride",
              ].map((area) => (
                <li key={area} className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-[#FF6B00]" />
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {currentYear} C.Glancey Double Glazing. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-gray-500 text-sm">
              <span>10 Year Guarantee on all products</span>
              <span className="hidden md:inline">|</span>
              <span>Quality & Service Guaranteed</span>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-600 text-sm">
              Site design and development by <a href="https://www.andywatsonit.co.uk/" target="_blank" rel="noopener noreferrer" className="text-[#FF6B00] hover:text-[#E65000] transition-colors">AndyWatsonIT</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
